import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import * as Sentry from '@sentry/react';
import useApi from '../utils/AxiosWrapper';
import { useUser } from '../utils/UserContext';

interface FlagSet {
  [flagKey: string]: boolean | string | number;
}

interface FeatureFlagContextType {
  flags: FlagSet;
  isLoading: boolean;
  error: Error | null;
  refetchFlags: () => Promise<void>;
  evaluateFlag: (flagKey: string) => boolean | string | number;
  reportFlagError: (flagKey: string, error: Error) => Promise<void>;
}

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(
  undefined
);

interface FeatureFlagProviderProps {
  children: ReactNode;
}

export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({
  children,
}) => {
  const { userId, userType } = useUser();
  const { callProtectedApi } = useApi();
  const [flags, setFlags] = useState<FlagSet>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchFlags = async () => {
    if (!userId || !userType) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await callProtectedApi(
        '/featureFlags/evaluate-batch',
        'post',
        {
          userId: userId,
          userType: userType,
          environment: process.env.REACT_APP_ENV || 'dev',
        }
      );

      if (response?.data?.data?.flags) {
        setFlags(response.data.data.flags);
      }
    } catch (err) {
      console.error('Error fetching feature flags:', err);
      setError(
        err instanceof Error ? err : new Error('Failed to fetch feature flags')
      );
      setFlags({});
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFlags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, userType]);

  const evaluateFlag = (flagKey: string): boolean | string | number => {
    return flags[flagKey] ?? false;
  };

  const reportFlagError = useCallback(
    async (flagKey: string, err: Error) => {
      Sentry.withScope((scope) => {
        scope.setTag('feature_flag', flagKey);
        Sentry.captureException(err);
      });

      try {
        await callProtectedApi(
          `/featureFlags/flags/${flagKey}/errors`,
          'post',
          {
            error_message: err.message,
            service_name: 'tach-ui',
            customer_id: userType === 'customer' ? userId : undefined,
            driver_id: userType === 'driver' ? userId : undefined,
            environment: process.env.REACT_APP_ENV || 'dev',
          }
        );
      } catch (reportErr) {
        console.error('Failed to report feature flag error:', reportErr);
      }
    },
    [callProtectedApi, userId, userType]
  );

  const value: FeatureFlagContextType = {
    flags,
    isLoading,
    error,
    refetchFlags: fetchFlags,
    evaluateFlag,
    reportFlagError,
  };

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

/**
 * Hook to access feature flags context
 * @returns Feature flag context
 */
export const useFeatureFlags = (): FeatureFlagContextType => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within FeatureFlagProvider');
  }
  return context;
};

/**
 * Hook to evaluate a single feature flag
 * @param flagKey - The feature flag key to evaluate
 * @returns The flag value (boolean, string, or number)
 */
export const useFeatureFlag = (flagKey: string): boolean | string | number => {
  const { evaluateFlag } = useFeatureFlags();
  return evaluateFlag(flagKey);
};

/**
 * Hook to check if a boolean feature flag is enabled
 * @param flagKey - The feature flag key to check
 * @returns True if the flag is enabled, false otherwise
 */
export const useFeatureFlagEnabled = (flagKey: string): boolean => {
  const value = useFeatureFlag(flagKey);
  return Boolean(value);
};

/**
 * Hook to get the flag error reporting function
 * @returns reportFlagError function
 */
export const useReportFlagError = () => {
  const { reportFlagError } = useFeatureFlags();
  return reportFlagError;
};

export default FeatureFlagProvider;
